import { queryOptions, useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
// oxlint-disable-next-line server-fn/no-create-server-fn -- SSR header reading for CloudFront region; will fold into analytics API later
import { createServerFn } from '@tanstack/react-start';
import { getRequestHeader } from '@tanstack/react-start/server';
import { useMemo } from 'react';
import { rootLogger } from '@/lib/root-logger';
import type { Iso3166_1_alpha_2_partial } from '@/lib/utils';
import { isFreePlanCountryCode } from '@listening/api-contract';
import { isRegisteredBeforeFreePlanCutoff } from '@listening/api-contract/free-plan-policy';
import { useFlag } from '@listening/frontend/analytics/client';
import { useAuthSession } from '@/services/auth-service';

const moduleLogger = rootLogger.child({
  module: 'useFreePlanAvailability',
});

export const getCloudfrontRegionFn = createServerFn().handler(
  () =>
    getRequestHeader('CloudFront-Viewer-Country') as
      | Iso3166_1_alpha_2_partial
      | undefined,
);

export const cloudfrontRegionQueryOptions = queryOptions({
  queryKey: ['cloudfront-region'],
  queryFn: () => getCloudfrontRegionFn().then((region) => region ?? null),
  staleTime: Infinity,
  gcTime: Infinity,
  retry: false,
});

// Legacy (pre-Better-Auth) users have no createdAt; derive it from the
// creation timestamp embedded in their MongoDB ObjectId.
function objectIdToTimestamp(objectId: string) {
  return new Date(Number.parseInt(objectId.slice(0, 8), 16) * 1000);
}

export function useFreePlan() {
  const { cloudfrontRegion, isFreePlanCountry } = useRouteContext({
    from: '__root__',
  });

  const regionQuery = useQuery({
    ...cloudfrontRegionQueryOptions,
    enabled: false,
  });

  const { user } = useAuthSession();
  const { value: ffSimulateFree } = useFlag('simulateFreeCountry');

  const isGrandfathered = useMemo(() => {
    try {
      if (!user) return false;
      // Better Auth provides createdAt directly; legacy derives from MongoDB ObjectId
      const creationDate = user.createdAt ?? objectIdToTimestamp(user.userId);
      return isRegisteredBeforeFreePlanCutoff(creationDate);
    } catch (error) {
      moduleLogger.error(
        { userId: user?.userId, error },
        'Error getting creation date',
      );
      return false;
    }
  }, [user]);

  const region = cloudfrontRegion ?? regionQuery.data ?? undefined;
  const isInFreePlanCountry =
    ffSimulateFree === 'true' ||
    (region === undefined
      ? (isFreePlanCountry ?? false)
      : isFreePlanCountryCode(region));
  const isResolvingCountry =
    region === undefined && regionQuery.fetchStatus === 'fetching';
  const isAvailable = isGrandfathered || isInFreePlanCountry;

  return {
    isAvailable,
    isInFreePlanCountry,
    isGrandfathered,
    isResolvingCountry,
  };
}
